<template>
    <div class="tool-panel-root">
        <transition name="tool-slide" @after-leave="$emit('close')">
            <div v-if="visible" ref="panel" class="tool-panel" :class="{ fullscreen: isFullscreen, dragging: isDragging }">
                <div class="resize-handle" @mousedown.prevent="startDrag" />
                <slot name="header" />
                <div class="panel-body">
                    <slot />
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
/**
 * Reusable slide-in panel container for toolbar tools.
 * Provides fullscreen toggle, drag-to-resize, and animated open/close.
 * Slots: #header (panel header), default (panel body content).
 * Emits: close (after leave transition completes).
 */
export default {
    data() {
        return {
            visible: false,
            isFullscreen: false,
            isDragging: false
        }
    },
    watch: {
        visible() {
            this.updateBodyClass()
        },
        isFullscreen() {
            this.updateBodyClass()
        }
    },
    mounted() {
        this.$nextTick(() => { this.visible = true })
        window.addEventListener('resize', this.updateBodyClass)
    },
    beforeUnmount() {
        document.body.classList.remove('tool-panel-open')
        window.removeEventListener('resize', this.updateBodyClass)
    },
    methods: {
        updateBodyClass() {
            const isOverlay = window.innerWidth <= 1200
            if (this.visible && !this.isFullscreen && !isOverlay) {
                document.body.classList.add('tool-panel-open')
            } else {
                document.body.classList.remove('tool-panel-open')
            }
        },
        toggleFullscreen() {
            this.isFullscreen = !this.isFullscreen
        },
        close() {
            this.visible = false
            document.body.classList.remove('tool-panel-open')
            document.documentElement.style.removeProperty('--tool-panel-width')
        },
        startDrag() {
            this.isDragging = true
            document.body.classList.add('dragging-panel')
            const onMouseMove = (e) => {
                const toolbarWidth = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--toolbar-width'))
                const width = Math.min(Math.max(window.innerWidth - e.clientX - toolbarWidth, 560), window.innerWidth * 0.5)
                document.documentElement.style.setProperty('--tool-panel-width', width + 'px')
            }
            const onMouseUp = () => {
                this.isDragging = false
                document.body.classList.remove('dragging-panel')
                document.removeEventListener('mousemove', onMouseMove)
                document.removeEventListener('mouseup', onMouseUp)
            }
            document.addEventListener('mousemove', onMouseMove)
            document.addEventListener('mouseup', onMouseUp)
        }
    },
    emits: ['close']
}
</script>

<style scoped>
.tool-panel {
    position: fixed;
    top: 0;
    right: var(--toolbar-width);
    bottom: 0;
    width: var(--tool-panel-width);
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 10;
    transition: width 0.25s ease;
}

.panel-body {
    flex: 1;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border-left: 1px solid #d9d9d9;
}

.tool-panel.dragging {
    transition: none;
    user-select: none;
}

.resize-handle {
    position: absolute;
    top: 0;
    left: 1px;
    bottom: 0;
    width: 5px;
    cursor: col-resize;
    z-index: 11;
}

.tool-panel.fullscreen {
    width: calc(100vw - var(--toolbar-width));
    z-index: 10000;
}

@media (max-width: 1200px) and (min-width: 769px) {
    .tool-panel {
        right: var(--toolbar-width);
        width: calc(100vw - var(--toolbar-width));
    }
    .resize-handle {
        display: none;
    }
}

@media (max-width: 768px) {
    .tool-panel {
        right: 0;
        width: 100vw;
    }
    .tool-panel.fullscreen {
        width: 100vw;
    }
    .resize-handle {
        display: none;
    }
}

.tool-slide-enter-active {
    transition: transform 0.25s ease;
}

.tool-slide-leave-active {
    transition: transform 0.25s ease !important;
}

.tool-slide-enter-from, .tool-slide-leave-to {
    transform: translateX(100%);
}

.tool-slide-enter-to, .tool-slide-leave-from {
    transform: translateX(0);
}
</style>

<style>
body.tool-panel-open .routa-content {
    right: calc(var(--tool-panel-width) + var(--toolbar-width)) !important;
    transition: right 0.25s ease;
}

body.tool-panel-open.dragging-panel .routa-content {
    transition: none;
}

@media (max-width: 1200px) {
    body.tool-panel-open .routa-content {
        right: 0 !important;
    }
}
</style>
